import type { ScreeningColumn } from '@app/src/pages/Findings/findingsUtils'
import { getScreeningColumns } from '@app/src/pages/Findings/findingsUtils'
import { useScreeningTemplates } from '@app/src/pages/Findings/useFindingsView'
import { Checkbox, FormControlLabel, Skeleton, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

interface SelectAdverseScreeningQuestionnairesForScrapingProps {
  name: string
}

type SelectedTemplate = { id: number; title: string }

const isColumnSelected = (column: ScreeningColumn, selectedIds: Set<number>): boolean =>
  column.templateIds.length > 0 && column.templateIds.every(id => selectedIds.has(id))

const toggleColumnSelection = (
  selected: SelectedTemplate[],
  column: ScreeningColumn,
  columnTemplates: SelectedTemplate[],
  checked: boolean,
): SelectedTemplate[] => {
  const withoutColumn = selected.filter(template => !column.templateIds.includes(template.id))
  return checked ? [...withoutColumn, ...columnTemplates] : withoutColumn
}

const SelectAdverseScreeningQuestionnairesForScraping: React.FC<
  SelectAdverseScreeningQuestionnairesForScrapingProps
> = ({ name }) => {
  const { formatMessage, messages } = useIntl()
  const { control } = useFormContext()
  const { screeningTemplates, isLoading } = useScreeningTemplates()

  const columns = useMemo(() => getScreeningColumns(screeningTemplates), [screeningTemplates])
  const allTemplates: SelectedTemplate[] = useMemo(
    () => screeningTemplates.map(template => ({ id: template.id, title: template.title })),
    [screeningTemplates],
  )

  if (isLoading) {
    return (
      <Stack spacing={2} width="100%">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={88} />
        ))}
      </Stack>
    )
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ onChange, value }) => {
        const selected: SelectedTemplate[] = value ?? []
        const selectedIds = new Set(selected.map(template => template.id))
        const allSelected = allTemplates.length > 0 && allTemplates.every(template => selectedIds.has(template.id))
        const someSelected = !allSelected && selected.length > 0

        return (
          <Stack spacing={2} width="100%">
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => onChange(allSelected ? [] : allTemplates)}
                />
              }
              label={
                <Typography fontWeight={600}>{formatMessage({ id: 'findings.aiScreeningModal.selectAll' })}</Typography>
              }
            />
            <Stack spacing={2}>
              {columns.map(column => {
                const columnTemplates = screeningTemplates
                  .filter(template => column.templateIds.includes(template.id))
                  .map(template => ({ id: template.id, title: template.title }))
                const checked = isColumnSelected(column, selectedIds)

                return (
                  <Stack key={column.type}>
                    <FormControlLabel
                      sx={{
                        m: 0,
                        px: 2,
                        py: 1.5,
                        border: 1,
                        borderColor: 'grey.300',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                      }}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => onChange(toggleColumnSelection(selected, column, columnTemplates, !checked))}
                        />
                      }
                      label={
                        <Typography fontWeight={600}>
                          {formatMessage({ id: column.titleId, defaultMessage: column.fallbackLabel })}
                        </Typography>
                      }
                    />
                    {(messages[column.descriptionId] || column.description) && (
                      <Stack
                        px={2}
                        py={1.5}
                        bgcolor="grey.100"
                        sx={{ borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {formatMessage({ id: column.descriptionId, defaultMessage: column.description ?? '' })}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                )
              })}
            </Stack>
          </Stack>
        )
      }}
    />
  )
}

export default SelectAdverseScreeningQuestionnairesForScraping
